import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import { TbTrash, TbEdit, TbX, TbCategory, TbPig, TbMoneybag, TbFileText, TbChecks, TbPlus } from 'react-icons/tb';
import { FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { SettingsPageTab } from './SettingsPage';
import CreateBankAccountModal from '../../components/modals/CreateBankAccountModal';
import OptionsButton from '../../components/buttons/OptionsButton';
import { OptionsButtonOption } from '../../../types/misc';
import {
  BankMutation,
  CreateBankTransactionModel,
  Currency,
  CreateJournalEntryDto,
  JournalEntryLineCategoryRef,
  JournalEntryModel,
  JournalEntrySourceType,
  JOURNAL_ENTRY_SOURCE_TYPES,
  LedgerAccount,
  LedgerAccountType,
  FinancialCategory,
} from '../../../types/bankAccount';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Avatar from '../../components/misc/Avatar';
import PageHeader from '../../components/misc/PageHeader';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import DatePicker from '../../components/forms/DatePicker';
import DropdownSelect from '../../components/forms/DropdownSelect';
import CategorySelect from '../../components/financial/CategorySelect';
import SimpleCategoryDropdown from '../../components/financial/SimpleCategoryDropdown';
import CreateCategoryModal from '../../components/financial/CreateCategoryModal';
import Button from '../../components/buttons/Button';
import {
  FinancialTable,
  FinancialDataCell,
  CompactDate,
  SortConfig,
} from '../../components/tables/FinancialTable';
import * as bankAccountService from '../../../services/community/bankAccountService';
import TransactionModal from '../../components/modals/TransactionModal';
import DeclarationModal from '../../components/modals/DeclarationModal';
import DeleteSplitTransactionWarningModal from '../../components/modals/DeleteSplitTransactionWarningModal';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { formatEuroCentsWithSign, calculateDisplayAmount, transactionToJournalEntryDto } from '../../../utils/bankAccountUtil';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import connectionService from '../../../services/community/connectionService';
import TransactionProcessor from '../../components/financial/TransactionProcessor';

interface TransactionsPageProps {
  hideHeader?: boolean;
}

export default function TransactionsPage({
  hideHeader = false,
}: TransactionsPageProps): JSX.Element | null {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.transaction' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const { t: tEmpty } = useTranslation('', { keyPrefix: 'page.financial.transaction.emptyState' });
  const { t: tFilters } = useTranslation('', { keyPrefix: 'page.financial.transaction.filters' });
  const { t: tResults } = useTranslation('', { keyPrefix: 'page.financial.transaction.results' });
  const { t: tSettings } = useTranslation('', { keyPrefix: 'page.financial.settings.tabs' });
  const { t: tCategories } = useTranslation('', { keyPrefix: 'page.financial.categories' });
  const { t: tAdd } = useTranslation('', { keyPrefix: 'page.financial.add' });
  const { t: tDeclaration } = useTranslation('', { keyPrefix: 'page.financial.declaration' });
  const navigate = useNavigate();
  const dashboardId = useSelector((state: RootState) => state.application.selectedDashboard);

  // Options for the Add button dropdown
  const addOptions: OptionsButtonOption[] = [
    {
      icon: TbFileText,
      name: tAdd('declaration') || tSettings('declarations'),
      onClick: () => setCreateDeclarationModalOpen(true),
    },
    {
      icon: TbMoneybag,
      name: tAdd('bankAccount') || tSettings('bankAccounts'),
      onClick: () => setCreateBankAccountModalOpen(true),
    },
    {
      icon: TbCategory,
      name: tAdd('category') || tSettings('categories'),
      onClick: () => navigate('/financial/settings', { state: { activeTab: SettingsPageTab.CATEGORIES } }),
    },
    {
      icon: TbPig,
      name: tAdd('pot') || tSettings('savingPots'),
      onClick: () => navigate('/financial/settings', { state: { activeTab: SettingsPageTab.SAVING_POTS } }),
    },
    {
      icon: TbMoneybag,
      name: tAdd('contributionPlan') || tSettings('contributions'),
      onClick: () => navigate('/financial/settings', { state: { activeTab: SettingsPageTab.CONTRIBUTIONS } }),
    },
  ];
  const [journalEntries, setJournalEntries] = useState<JournalEntryModel[] | null>();
  const community = useSelectedCommunity();
  const connections = useSelector((state: RootState) => state.connections);
  const [search, setSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start?: Date; end?: Date }>({});
  const [typeFilter, setTypeFilter] = useState<JournalEntrySourceType | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PROCESSED' | 'UNPROCESSED'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [categoryFilterMenuOpen, setCategoryFilterMenuOpen] = useState<boolean>(false);
  const [createCategoryModalOpen, setCreateCategoryModalOpen] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'date',
    direction: 'desc',
  });
  const [splitModalJournalEntry, setSplitModalJournalEntry] = useState<JournalEntryModel | null>(null);
  const [splitModalOpen, setSplitModalOpen] = useState<boolean>(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState<boolean>(false);
  const [transactionToDelete, setTransactionToDelete] =
    useState<JournalEntryModel | null>(null);
  const [mutations, setMutations] = useState<BankMutation[] | null>(null);
  const [createDeclarationModalOpen, setCreateDeclarationModalOpen] = useState<boolean>(false);
  const [createBankAccountModalOpen, setCreateBankAccountModalOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<FinancialCategory[]>([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [editingCategoryForEntryId, setEditingCategoryForEntryId] = useState<number | null>(null);
  const [bulkCategoryId, setBulkCategoryId] = useState<number | null>(null);
  const [bulkAssigning, setBulkAssigning] = useState<boolean>(false);

  useEffect(() => {
    bankAccountService.getAllJournalEntries().then(setJournalEntries);
    connectionService.getConnections();
  }, [community]);

  useEffect(() => {
    if (community) {
      // Fetch mutations to check for split transactions
      bankAccountService.getAllBankAccounts().then((accounts) => {
        if (accounts.length > 0) {
          bankAccountService.getAllMutationsForBank(accounts[0].accountId).then(setMutations);
        }
      });
    }
  }, [community]);

  useEffect(() => {
    if (dashboardId != null) {
      bankAccountService.getCategories().then(setCategories).catch(() => setCategories([]));
    } else {
      setCategories([]);
    }
  }, [dashboardId]);

  const getLineIdsForEntry = (entry: JournalEntryModel): number[] => {
    if (!entry.lines || entry.lines.length === 0) return [];
    return entry.lines.map((l) => l.journalEntryLineId);
  };

  const getPrimaryCategoryForEntry = (
    entry: JournalEntryModel,
  ): JournalEntryLineCategoryRef | null => {
    const lineWithCategory = (entry.lines ?? []).find((l) => l.categories && l.categories.length > 0);
    return lineWithCategory?.categories?.[0] ?? null;
  };

  const toggleSelectedEntry = (entryId: number) => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedEntryIds(new Set());

  const handleInlineCategoryChange = async (entry: JournalEntryModel, categoryId: number) => {
    const lineIds = getLineIdsForEntry(entry);
    if (lineIds.length === 0) {
      toast.error(t('error.noLinesToCategorize'));
      return;
    }
    try {
      await bankAccountService.assignCategoryToLines(lineIds, categoryId);
      setEditingCategoryForEntryId(null);
      const refreshed = await bankAccountService.getAllJournalEntries();
      setJournalEntries(refreshed);
      toast.success(t('success.categoryAssigned'));
    } catch (error: any) {
      toast.error(
        t('error.categoryAssignFailed', { error: error?.response?.data?.message ?? error?.message ?? String(error) }),
      );
    }
  };

  const handleBulkAssign = async () => {
    if (selectedEntryIds.size === 0 || bulkCategoryId == null) return;
    setBulkAssigning(true);
    try {
      const selectedEntries = (journalEntries ?? []).filter((e) => selectedEntryIds.has(e.journalEntryId));
      const lineIds: number[] = [];
      selectedEntries.forEach((e) => {
        getLineIdsForEntry(e).forEach((id) => lineIds.push(id));
      });
      if (lineIds.length === 0) {
        toast.error(t('error.noLinesToCategorize'));
        setBulkAssigning(false);
        return;
      }
      await bankAccountService.assignCategoryToLines(lineIds, bulkCategoryId);
      const refreshed = await bankAccountService.getAllJournalEntries();
      setJournalEntries(refreshed);
      toast.success(t('success.bulkCategoryAssigned', { count: selectedEntries.length }));
      clearSelection();
      setBulkCategoryId(null);
    } catch (error: any) {
      toast.error(
        t('error.categoryAssignFailed', { error: error?.response?.data?.message ?? error?.message ?? String(error) }),
      );
    } finally {
      setBulkAssigning(false);
    }
  };

  // Get member names for search
  const getMemberName = (connectionId: number): string => {
    const connection = connections.find((c) => +c.dashboardMemberId === connectionId);
    if (!connection) return '';
    return `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`.toLowerCase();
  };

  const getOppositeNameForTransaction = (
    entry: JournalEntryModel,
  ): string | undefined => {
    if (!entry.mutationId || !mutations) return undefined;

    return mutations.find((m) => m.mutationId === entry.mutationId)?.oppositeName;
  };

  // Filter and sort journal entries
  const filteredAndSortedEntries = useMemo(() => {
    if (!journalEntries) return [];

    let filtered = [...journalEntries];

    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((entry) => {
        // Search in description
        if (entry.description.toLowerCase().includes(searchLower)) return true;

        // Search in oppositeName from mutation
        const oppositeName = getOppositeNameForTransaction(entry);
        if (oppositeName && oppositeName.toLowerCase().includes(searchLower)) return true;

        // Search in formatted amount
        const displayAmount = calculateDisplayAmount(entry);
        const formattedAmount = formatEuroCentsWithSign(displayAmount, i18n.language);
        if (formattedAmount.toLowerCase().includes(searchLower)) return true;

        // Search in member names
        if (entry.payerId) {
          if (getMemberName(entry.payerId).includes(searchLower)) return true;
        }
        if (entry.lines) {
          const hasMatchingMember = entry.lines.some((line) =>
            line.participantMemberId && getMemberName(line.participantMemberId).includes(searchLower),
          );
          if (hasMatchingMember) return true;
        }

        return false;
      });
    }

    // Apply date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.entryDate);
        if (dateRange.start && entryDate < dateRange.start) return false;
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          if (entryDate > endDate) return false;
        }
        return true;
      });
    }

    // Apply type filter
    if (typeFilter !== 'ALL') {
      filtered = filtered.filter((entry) => entry.sourceType === typeFilter);
    }

    // Apply status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((entry) => {
        const isProcessed = !!entry.processedByDashboardMemberId;
        return statusFilter === 'PROCESSED' ? isProcessed : !isProcessed;
      });
    }

    // Apply category filter (entry matches if any of its lines has the category)
    if (categoryFilter != null) {
      filtered = filtered.filter((entry) =>
        (entry.lines ?? []).some((line) =>
          (line.categories ?? []).some((c) => c.category.financialCategoryId === categoryFilter),
        ),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.column) {
        case 'date':
          comparison =
            new Date(a.entryDate).getTime() - new Date(b.entryDate).getTime();
          break;
        case 'amount':
          comparison = calculateDisplayAmount(a) - calculateDisplayAmount(b);
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
        case 'type':
          comparison = a.sourceType.localeCompare(b.sourceType);
          break;
        default:
          comparison = 0;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [journalEntries, search, dateRange, typeFilter, statusFilter, categoryFilter, sortConfig, connections, i18n.language, mutations, categories]);

  const handleSort = (config: SortConfig) => {
    setSortConfig(config);
  };

  const selectableVisibleCount = useMemo(
    () =>
      filteredAndSortedEntries.filter((entry) => (entry.lines ?? []).length > 0).length,
    [filteredAndSortedEntries],
  );

  const selectAllVisible = () => {
    const selectableIds = filteredAndSortedEntries
      .filter((entry) => (entry.lines ?? []).length > 0)
      .map((entry) => entry.journalEntryId);
    setSelectedEntryIds(new Set(selectableIds));
  };

  const clearFilters = () => {
    setSearch('');
    setDateRange({});
    setTypeFilter('ALL');
    setStatusFilter('ALL');
    setCategoryFilter(null);
  };

  const hasActiveFilters = search.trim() || dateRange.start || dateRange.end || typeFilter !== 'ALL' || statusFilter !== 'ALL' || categoryFilter != null;
  const showBulkBar = selectedEntryIds.size > 0;

  const openTransactionModal = (entry: JournalEntryModel) => {
    setSplitModalJournalEntry(entry);
    setTimeout(() => setSplitModalOpen(true), 10);
  };

  const isEntryFromSplitMutation = (
    entry: JournalEntryModel,
  ): boolean => {
    if (!entry.mutationId || !mutations) return false;

    const mutation = mutations.find((m) => m.mutationId === entry.mutationId);
    return mutation ? mutation.journalEntryCount > 1 : false;
  };

  const handleDeleteTransaction = (entry: JournalEntryModel) => {
    if (isEntryFromSplitMutation(entry)) {
      setTransactionToDelete(entry);
      setDeleteWarningModalOpen(true);
      setSplitModalOpen(false);
      setTimeout(() => setSplitModalJournalEntry(null), 0);
    } else {
      performDeleteTransaction(entry);
    }
  };

  const performDeleteTransaction = (entry: JournalEntryModel) => {
    bankAccountService
      .deleteJournalEntry(entry.journalEntryId)
      .then(() => {
        setJournalEntries((prev) =>
          prev ? prev.filter((e) => e.journalEntryId !== entry.journalEntryId) : prev,
        );
        toast.success(t('success.deleted'));
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status >= 200 && status < 300) {
          setJournalEntries((prev) =>
            prev ? prev.filter((e) => e.journalEntryId !== entry.journalEntryId) : prev,
          );
          toast.success(t('success.deleted'));
        } else {
          toast.error(t('error.deletionFailed', { error: error?.message ?? String(error) }));
        }
      });
  };

  const performDeleteAllTransactionsForMutation = (
    entry: JournalEntryModel,
  ) => {
    if (!entry.mutationId || !journalEntries) return;

    // Find all entries with the same mutationId
    const entriesToDelete = journalEntries.filter(
      (e) => e.mutationId === entry.mutationId,
    );

    // Delete all entries with the same mutationId
    const deletePromises = entriesToDelete.map((e) =>
      bankAccountService.deleteJournalEntry(e.journalEntryId),
    );

    Promise.all(deletePromises)
      .then(() => {
        setJournalEntries((prev) =>
          prev ? prev.filter((e) => e.mutationId !== entry.mutationId) : prev,
        );
        setDeleteWarningModalOpen(false);
        setTransactionToDelete(null);
        toast.success(t('success.deleted'));
      })
      .catch((error) => {
        // Backend may return 2xx (e.g. 204 No Content) but axios or response handling can throw
        const status = error?.response?.status;
        if (status >= 200 && status < 300) {
          setJournalEntries((prev) =>
            prev ? prev.filter((e) => e.mutationId !== entry.mutationId) : prev,
          );
          setDeleteWarningModalOpen(false);
          setTransactionToDelete(null);
          toast.success(t('success.deleted'));
        } else {
          toast.error(t('error.deletionFailed', { error: error?.message ?? String(error) }));
        }
      });
  };

  if (!community) return null;

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <OptionsButton
              options={addOptions}
              containerClassName="left-0 sm:right-0 sm:left-auto"
              className="bg-primary text-secondary-50 flex h-fit w-fit items-center gap-2 whitespace-nowrap rounded-[12px] px-[16px] py-[8px] font-[600] transition-all hover:bg-opacity-80 hover:text-opacity-80">
              <FaChevronDown className="h-5 w-5" />
              {tAdd('add') || 'Toevoegen'}
            </OptionsButton>
          }
        />
      )}

      {/* Filter Bar */}
      {journalEntries && journalEntries.length > 0 && (
        <div className="mb-4 space-y-2">
          {/* Search and Quick Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <SearchBar className="max-w-[320px] flex-1 min-w-[180px]" search={search} setSearch={setSearch} />
            {hasActiveFilters && (
              <Button variant="secondary" onClick={clearFilters} className="flex items-center gap-2">
                <TbX className="h-4 w-4" />
                {tFilters('clearFilters')}
              </Button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-0.5">
                <label className="text-xs font-medium">{tFilters('from')}</label>
                <DatePicker
                  date={dateRange.start}
                  setDate={(date) => setDateRange((prev) => ({ ...prev, start: date }))}
                  includeTime={false}
                  customFormat="DD-MM-YYYY"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-xs font-medium">{tFilters('to')}</label>
                <DatePicker
                  date={dateRange.end}
                  setDate={(date) => setDateRange((prev) => ({ ...prev, end: date }))}
                  includeTime={false}
                  customFormat="DD-MM-YYYY"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="min-w-[160px]">
              <div className="flex flex-col gap-0.5">
                <label className="text-xs font-medium">{tFilters('type')}</label>
                <DropdownSelect
                  options={[
                    { label: tFilters('allTypes'), value: 'ALL' as JournalEntrySourceType | 'ALL' },
                    { label: tEnum('JournalEntrySourceType.PERSONAL_INCOME'), value: JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME },
                    { label: tEnum('JournalEntrySourceType.PERSONAL_EXPENSE'), value: JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE },
                    { label: tEnum('JournalEntrySourceType.GROUP_INCOME'), value: JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME },
                    { label: tEnum('JournalEntrySourceType.GROUP_EXPENSE'), value: JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE },
                    { label: tEnum('JournalEntrySourceType.CONTRIBUTION'), value: JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN },
                    { label: tEnum('JournalEntrySourceType.INTERNAL_BANK_TRANSFER'), value: JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER },
                  ]}
                  value={typeFilter}
                  setValue={(value) => setTypeFilter(value || 'ALL')}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="min-w-[150px]">
              <div className="flex flex-col gap-0.5">
                <label className="text-xs font-medium">{tFilters('status')}</label>
                <DropdownSelect
                  options={[
                    { label: tFilters('allStatus'), value: 'ALL' as 'ALL' | 'PROCESSED' | 'UNPROCESSED' },
                    { label: tFilters('processed'), value: 'PROCESSED' as 'ALL' | 'PROCESSED' | 'UNPROCESSED' },
                    { label: tFilters('unprocessed'), value: 'UNPROCESSED' as 'ALL' | 'PROCESSED' | 'UNPROCESSED' },
                  ]}
                  value={statusFilter}
                  setValue={(value) => setStatusFilter(value || 'ALL')}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="min-w-[180px] max-w-[260px]">
              <div className="flex flex-col gap-0.5">
                <label className="text-xs font-medium">{tFilters('category')}</label>
                <DropdownSelect<number | null>
                  key={`category-filter-${categories.length}`}
                  options={[
                    { label: tFilters('allCategories'), value: null },
                    ...categories.map((c) => ({
                      label: c.name,
                      value: c.financialCategoryId as number | null,
                    })),
                  ]}
                  value={categoryFilter}
                  setValue={(v) => setCategoryFilter(v ?? null)}
                  placeholder={tFilters('allCategories')}
                  isClearable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                  onMenuOpen={() => setCategoryFilterMenuOpen(true)}
                  onMenuClose={() => setCategoryFilterMenuOpen(false)}
                />
                {categoryFilterMenuOpen && categories.length === 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryFilterMenuOpen(false);
                      setCreateCategoryModalOpen(true);
                    }}
                    className="mt-1 flex items-center gap-1 self-start text-xs font-medium text-primary-700 hover:underline">
                    <TbPlus className="h-3 w-3" />
                    {tCategories?.('createInlineOption') ?? 'Create new category'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      {journalEntries && journalEntries.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-xs text-primary-900">
          <span>
            {tResults('showing', {
              count: filteredAndSortedEntries.length,
              total: journalEntries.length,
            })}
          </span>
          {hasActiveFilters && (
            <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-900">
              {tFilters('activeFilters')}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {!journalEntries ? (
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="h-9 w-full max-w-xs rounded bg-gray-100 animate-pulse" />
              <div className="h-9 w-40 rounded bg-gray-100 animate-pulse" />
              <div className="h-9 w-40 rounded bg-gray-100 animate-pulse" />
              <div className="h-9 w-40 rounded bg-gray-100 animate-pulse" />
            </div>
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-100">
              <div className="h-10 border-b border-gray-100 bg-gray-50" />
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="flex gap-4 border-b border-gray-50 px-4 py-3">
                  <div className="h-4 w-20 rounded bg-gray-100 animate-pulse" />
                  <div className="h-4 flex-1 rounded bg-gray-100 animate-pulse" />
                  <div className="h-4 w-24 rounded bg-gray-100 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        ) : journalEntries.length === 0 ? (
          <EmptyScreenView title={tEmpty('title')} subtitle={tEmpty('subtitle')} />
        ) : filteredAndSortedEntries.length === 0 ? (
          <EmptyScreenView
            title={tResults('noResults')}
            subtitle={tResults('clearFiltersToSeeMore')}
            primaryButton={
              hasActiveFilters ? (
                <Button variant="primary-dark" onClick={clearFilters}>
                  {tFilters('clearFilters')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {showBulkBar && (
              <div
                className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary-300 bg-primary-50 bg-opacity-30 px-4 py-2"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  <TbChecks className="h-5 w-5 text-primary-700" />
                  <span className="text-sm font-medium text-primary-900">
                    {t('bulk.selectedCount', { count: selectedEntryIds.size })}
                  </span>
                </div>
                <div className="min-w-[220px] basis-[220px]">
                  <SimpleCategoryDropdown
                    categories={categories}
                    value={bulkCategoryId}
                    setValue={(v) => setBulkCategoryId(v)}
                    placeholder={t('bulk.assignCategoryPlaceholder')}
                    onCategoryCreated={(c) => setCategories((prev) => [...prev, c])}
                  />
                </div>
                <Button
                  variant="primary-dark"
                  disabled={bulkCategoryId == null || bulkAssigning}
                  onClick={handleBulkAssign}>
                  {bulkAssigning ? t('bulk.assigning') : t('bulk.apply')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={selectableVisibleCount === selectedEntryIds.size}
                  onClick={selectAllVisible}>
                  {t('bulk.selectAll', {
                    count: selectableVisibleCount,
                    defaultValue: `Select all (${selectableVisibleCount})`,
                  })}
                </Button>
                <Button variant="secondary" onClick={clearSelection}>
                  {t('bulk.clear')}
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-8">
              <FinancialTable
                columns={[
                  { id: 'select', label: '' },
                  { id: 'date', label: t('table.date'), sortable: true },
                  { id: 'description', label: t('table.description'), sortable: true },
                  { id: 'amount', label: t('table.amount'), sortable: true },
                  { id: 'type', label: t('table.type'), sortable: true },
                  { id: 'category', label: t('table.category') },
                  { id: 'assignedTo', label: t('table.assignedTo') },
                  { id: 'processedBy', label: t('table.processedBy') },
                  { id: 'actions', label: t('table.actions') },
                ]}
                rows={filteredAndSortedEntries}
                sortConfig={sortConfig}
                onSortChange={handleSort}
                renderRow={(entry) => {
                  const displayAmount = calculateDisplayAmount(entry);
                  const isSelected = selectedEntryIds.has(entry.journalEntryId);
                  const editingCategory = editingCategoryForEntryId === entry.journalEntryId;
                  const entryLineIds = getLineIdsForEntry(entry);
                  const canCategorize = entryLineIds.length > 0;
                  return (
                    <>
                      <FinancialDataCell
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectedEntry(entry.journalEntryId);
                        }}
                        className="w-10 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectedEntry(entry.journalEntryId)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 cursor-pointer rounded border-secondary-300 text-primary focus:ring-primary"
                        />
                      </FinancialDataCell>
                      <FinancialDataCell
                        onClick={() => openTransactionModal(entry)}
                        className="whitespace-nowrap">
                        <CompactDate date={new Date(entry.entryDate)} />
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openTransactionModal(entry)}>
                        <div className="flex flex-col gap-1">
                          <p className="text-sm">
                            {getOppositeNameForTransaction(entry)}
                            {entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && (
                              <>
                                {' - '}
                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-300 bg-opacity-20 text-primary-900">
                                  Contribution
                                </span>
                              </>
                            )}
                          </p>
                          <p className="max-w-md truncate text-gray-400" title={entry.description}>
                            {entry.description}
                          </p>
                        </div>
                      </FinancialDataCell>
                      <FinancialDataCell
                        onClick={() => openTransactionModal(entry)}
                        className={`whitespace-nowrap text-right font-semibold ${
                          displayAmount > 0 ? 'text-success' : 'text-danger'
                        }`}>
                        {formatEuroCentsWithSign(displayAmount, i18n.language)}
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openTransactionModal(entry)} className="whitespace-nowrap">
                        <TypeBadge
                          sourceType={entry.sourceType}
                          tEnum={tEnum}
                        />
                      </FinancialDataCell>
                      <FinancialDataCell
                        onClick={(e) => {
                          if (!canCategorize) return;
                          e.stopPropagation();
                          setEditingCategoryForEntryId(
                            editingCategory ? null : entry.journalEntryId,
                          );
                        }}
                        className="min-w-[160px] whitespace-nowrap">
                        {canCategorize ? (
                          <CategoryCell
                            assigned={getPrimaryCategoryForEntry(entry)}
                            editing={editingCategory}
                            onStartEdit={() => setEditingCategoryForEntryId(entry.journalEntryId)}
                            onCancelEdit={() => setEditingCategoryForEntryId(null)}
                            onSelect={(categoryId) => {
                              handleInlineCategoryChange(entry, categoryId).catch(() => undefined);
                            }}
                            categories={categories}
                            onCategoryCreated={(c) => setCategories((prev) => [...prev, c])}
                            placeholder={t('table.chooseCategory')}
                          />
                        ) : (
                          <span className="text-xs text-secondary-300">—</span>
                        )}
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openTransactionModal(entry)}>
                        {displayAmount > 0 ? (
                          <PayerInfo entry={entry} />
                        ) : (
                          <PayeeInfo entry={entry} />
                        )}
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openTransactionModal(entry)}>
                        <TransactionProcessor entry={entry} />
                      </FinancialDataCell>
                      <FinancialDataCell>
                        <ActionMenu>
                          <ActionMenuButton
                            icon={TbEdit}
                            text={t('table.edit')}
                            onClick={() => openTransactionModal(entry)}
                          />
                          <ActionMenuButton
                            icon={TbTrash}
                            text={t('table.delete')}
                            className="text-danger"
                            onClick={() => handleDeleteTransaction(entry)}
                          />
                        </ActionMenu>
                      </FinancialDataCell>
                    </>
                  );
                }}
              />
            </div>
          </>
        )}
        {/* Note: Modals will need to be updated separately to work with journal entries */}
        {splitModalJournalEntry && (
          <TransactionModal
            open={splitModalOpen}
            setOpen={(open) => {
              if (!open) {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalJournalEntry(null), 200);
              }
            }}
            initialJournalEntry={splitModalJournalEntry as any}
            onSplitSuccess={async (payload) => {
              // Handle delete-side callbacks that send an empty array
              if (!payload || (Array.isArray(payload) && payload.length === 0)) {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalJournalEntry(null), 200);
                return;
              }

              // Split editing from this page is not supported yet
              if (Array.isArray(payload)) {
                toast.error(t('error.splitEditNotSupported'));
                return;
              }

              // Single journal entry edit/create
              const dto = payload as CreateJournalEntryDto;

              try {
                if (splitModalJournalEntry?.journalEntryId) {
                  await bankAccountService.updateJournalEntry(
                    splitModalJournalEntry.journalEntryId,
                    dto,
                  );
                } else {
                  await bankAccountService.createJournalEntry(dto);
                }

                const refreshed = await bankAccountService.getAllJournalEntries();
                setJournalEntries(refreshed);
                toast.success(t('success.updated'));
              } catch (error: any) {
                toast.error(
                  t('error.updateFailed', { error: error?.message ?? String(error) }),
                );
              } finally {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalJournalEntry(null), 200);
              }
            }}
          />
        )}

        <DeleteSplitTransactionWarningModal
          open={deleteWarningModalOpen}
          setOpen={setDeleteWarningModalOpen}
          transaction={transactionToDelete as any}
          onConfirmDeleteAll={() => {
            if (transactionToDelete) {
              performDeleteAllTransactionsForMutation(transactionToDelete);
            }
          }}
        />

        <DeclarationModal
          open={createDeclarationModalOpen}
          setOpen={setCreateDeclarationModalOpen}
          onSplitSuccess={(declaration: CreateBankTransactionModel) => {
            if (!dashboardId) {
              toast.error(t('error.declarationCreation'));
              return;
            }
            bankAccountService
              .getWalletAccounts()
              .then((accounts: LedgerAccount[]) => {
                const walletAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.ASSET && acc.dashboardMemberId === null,
                );
                const expenseAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.EXPENSE,
                );
                const incomeAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.REVENUE,
                );
                
                const dto = transactionToJournalEntryDto(
                  declaration,
                  expenseAccount?.ledgerAccountId,
                  incomeAccount?.ledgerAccountId,
                  walletAccount?.ledgerAccountId,
                  Currency.EUR,
                );
                
                return bankAccountService.createJournalEntry({
                  ...dto,
                  dashboardId,
                });
              })
              .then(() => {
                return bankAccountService.getAllJournalEntries();
              })
              .then((entries) => {
                setJournalEntries(entries);
                toast.success(tDeclaration('success.created'));
                setCreateDeclarationModalOpen(false);
              })
              .catch((error) => {
                const errorMessage = error?.message || t('error.declarationCreation');
                toast.error(errorMessage);
                console.error('Failed to create declaration:', error);
              });
          }}
        />

        <CreateBankAccountModal
          open={createBankAccountModalOpen}
          setOpen={setCreateBankAccountModalOpen}
          onSuccess={(bankData) => {
            bankAccountService
              .createBankAccount(bankData)
              .then(() => {
                toast.success(t('success.bankAccountCreated'));
                setCreateBankAccountModalOpen(false);
                // Optionally navigate to bank accounts page
                navigate('/financial/overview');
              })
              .catch((error) => {
                toast.error(t('error.bankAccountCreateFailed', { error: error.message }));
              });
          }}
        />
        <CreateCategoryModal
          open={createCategoryModalOpen}
          setOpen={setCreateCategoryModalOpen}
          onCreated={(c: FinancialCategory) => {
            setCategories((prev) => [...prev, c]);
            setCategoryFilter(c.financialCategoryId);
          }}
        />
      </div>
    </>
  );
}


interface CategoryCellProps {
  assigned: JournalEntryLineCategoryRef | null;
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSelect: (categoryId: number) => void;
  categories: FinancialCategory[];
  onCategoryCreated: (c: FinancialCategory) => void;
  placeholder: string;
}

function CategoryCell({
  assigned,
  editing,
  onStartEdit,
  onCancelEdit,
  onSelect,
  categories,
  onCategoryCreated,
  placeholder,
}: CategoryCellProps): JSX.Element {
  if (editing) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <CategorySelect
          categories={categories}
          value={undefined}
          setValue={(v) => {
            if (v != null) onSelect(v);
            else onCancelEdit();
          }}
          placeholder={placeholder}
          autoFocus
          menuIsOpen
          onCategoryCreated={onCategoryCreated}
          onChange={() => {
            /* value applied via setValue */
          }}
        />
      </div>
    );
  }
  if (assigned) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStartEdit();
        }}
        className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-white px-2 py-0.5 text-xs font-medium text-primary-900 hover:bg-secondary-50">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: assigned.category.color }}
        />
        {assigned.category.name}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onStartEdit();
      }}
      className="inline-flex items-center gap-2 rounded-full border border-dashed border-secondary-300 px-2 py-0.5 text-xs font-medium text-primary-900 hover:bg-secondary-50">
      <TbCategory className="h-3 w-3" />
      {placeholder}
    </button>
  );
}

function PayerInfo({
  entry,
}: {
  entry: JournalEntryModel;
}): JSX.Element | null {
  const connections = useSelector((state: RootState) => state.connections);
  const community = useSelectedCommunity();
  const isDeclaration = !!entry.payerId;
  const isContribution = entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;

  if (!community) return null;

  // For declarations, show both payer and participants
  if (isDeclaration) {
    const payer = connections.find((x) => +x.dashboardMemberId === entry.payerId);
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    
    // If there are participants, show them; otherwise show payer
    if (participantLines.length > 0) {
      return <AvatarListFromLines lines={participantLines} />;
    }
    
    if (payer) {
      const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;
      const fullName = `${payer.fields.FIRST_NAME} ${payer.fields.LAST_NAME}`;

      return (
        <div title={fullName}>
          <Avatar
            className="h-7 w-7 rounded-full"
            src={payer?.picture}
            alias={alias}
          />
        </div>
      );
    }
  }
  if (isContribution && entry.dashboardMemberId) {
    const payer = connections.find((x) => +x.dashboardMemberId === entry.dashboardMemberId);
    if (!payer) return null;

    const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;
    const fullName = `${payer.fields.FIRST_NAME} ${payer.fields.LAST_NAME}`;

    return (
      <div title={fullName}>
        <Avatar
          className="h-7 w-7 rounded-full"
          src={payer?.picture}
          alias={alias}
        />
      </div>
    );
  }

  // For saving pot fund transactions, show who funded it
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_FUND) {
    // Check for participantMemberId in lines first
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    if (participantLines.length > 0) {
      return <AvatarListFromLines lines={participantLines} />;
    }
    // Otherwise check dashboardMemberId
    if (entry.dashboardMemberId) {
      const funder = connections.find((x) => +x.dashboardMemberId === entry.dashboardMemberId);
      if (funder) {
        const alias = `${funder.fields.FIRST_NAME}+${funder.fields.LAST_NAME}`;
        const fullName = `${funder.fields.FIRST_NAME} ${funder.fields.LAST_NAME}`;
        return (
          <div title={fullName}>
            <Avatar
              className="h-7 w-7 rounded-full"
              src={funder?.picture}
              alias={alias}
            />
          </div>
        );
      }
    }
    // Fallback to community
    return (
      <div title={community.name}>
        <Avatar
          className="h-7 w-7"
          src={community.picture ?? undefined}
          alias={community.name}
        />
      </div>
    );
  }

  if (
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME
  ) {
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    return (
      <>
        {participantLines.length === 0 ? (
          <div title={community.name}>
            <Avatar
              className="h-7 w-7"
              src={community.picture ?? undefined}
              alias={community.name}
            />
          </div>
        ) : (
          <AvatarListFromLines lines={participantLines} />
        )}
      </>
    );
  }

  return null;
}

function PayeeInfo({
  entry,
}: {
  entry: JournalEntryModel;
}): JSX.Element | null {
  const connections = useSelector((state: RootState) => state.connections);
  const community = useSelectedCommunity();
  const isDeclaration = !!entry.payerId;

  if (!community) return null;

  // For declarations, show participants (who it was paid for)
  if (isDeclaration) {
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    if (participantLines.length > 0) {
      return <AvatarListFromLines lines={participantLines} />;
    }
    // If no participants, show community (group expense)
    return (
      <div title={community.name}>
        <Avatar
          className="h-7 w-7"
          src={community.picture ?? undefined}
          alias={community.name}
        />
      </div>
    );
  }

  // For saving pot release transactions, show who released it
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_RELEASE) {
    // Check for participantMemberId in lines first
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    if (participantLines.length > 0) {
      return <AvatarListFromLines lines={participantLines} />;
    }
    // Otherwise check dashboardMemberId
    if (entry.dashboardMemberId) {
      const releaser = connections.find((x) => +x.dashboardMemberId === entry.dashboardMemberId);
      if (releaser) {
        const alias = `${releaser.fields.FIRST_NAME}+${releaser.fields.LAST_NAME}`;
        const fullName = `${releaser.fields.FIRST_NAME} ${releaser.fields.LAST_NAME}`;
        return (
          <div title={fullName}>
            <Avatar
              className="h-7 w-7 rounded-full"
              src={releaser?.picture}
              alias={alias}
            />
          </div>
        );
      }
    }
    // Fallback to community
    return (
      <div title={community.name}>
        <Avatar
          className="h-7 w-7"
          src={community.picture ?? undefined}
          alias={community.name}
        />
      </div>
    );
  }

  if (
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE ||
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE
  ) {
    const participantLines = entry.lines?.filter((l) => l.participantMemberId) || [];
    return (
      <>
        {participantLines.length === 0 ? (
          <div title={community.name}>
            <Avatar
              className="h-7 w-7"
              src={community.picture ?? undefined}
              alias={community.name}
            />
          </div>
        ) : (
          <AvatarListFromLines lines={participantLines} />
        )}
      </>
    );
  }

  return null;
}


function TypeBadge({
  sourceType,
  tEnum,
}: {
  sourceType: JournalEntrySourceType;
  tEnum: (key: string) => string;
}): JSX.Element {
  const isIncome =
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;

  const typeLabel = tEnum(`JournalEntrySourceType.${sourceType}`) || sourceType;

  return (
    <span
      className={twMerge(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        isIncome
          ? 'bg-primary-300 bg-opacity-20 text-primary-900'
          : 'bg-secondary bg-opacity-20 text-primary-900',
      )}>
      {typeLabel}
    </span>
  );
}

function AvatarListFromLines({ lines }: { lines: Array<{ participantMemberId: number | null }> }) {
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER);

  const displayParticipants = lines
    .filter((l) => l.participantMemberId)
    .map((l) => connections.find((connection) => +connection.dashboardMemberId === +l.participantMemberId!))
    .filter(Boolean);

  // Create tooltip text with all participant names
  const tooltipText = displayParticipants
    .map((c) => `${c?.fields.FIRST_NAME} ${c?.fields.LAST_NAME}`)
    .join(', ');

  return (
    <div className="flex items-center gap-[-8px]" title={tooltipText}>
      {displayParticipants.length === 1 ? (
        <div title={`${displayParticipants[0]?.fields.FIRST_NAME} ${displayParticipants[0]?.fields.LAST_NAME}`}>
          <Avatar
            className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-secondary-50 shadow-md"
            src={displayParticipants[0]?.picture}
            alias={`${displayParticipants[0]?.fields.FIRST_NAME}+${displayParticipants[0]?.fields.LAST_NAME}`}
          />
        </div>
      ) : (
        <>
          {displayParticipants
            .slice(0, 2)
            .map((c, index) => (
              <div key={`avatar_wrapper_${index}_${c?.dashboardMemberId}`} title={tooltipText}>
                <Avatar
                  className={`relative z-[${10 - index}] flex h-7 w-7 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
                  src={c?.picture}
                  alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
                />
              </div>
            ))}
          {lines.length > 2 && (
            <div
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-white shadow-md"
              style={{
                marginLeft: '-8px',
                zIndex: 1,
              }}
              title={tooltipText}>
              +{lines.length - 2}
            </div>
          )}
        </>
      )}
    </div>
  );
}

